import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { RefreshCw, Search } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../../common/breadcrumb";
import { fetchAdminHiddenModifiers, fetchBranches } from "../../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader } from "../../reporting/ReportingShared";
import { isMerchantScoped } from "../../../constants/roles";

const HiddenModifiers = () => {
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [displayedBranches, setDisplayedBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ─── Branches ─────────────────────────────────────────────
  const fetchBranchesData = useCallback(async () => {
    try {
      const response = await fetchBranches();
      setBranches(response.data || []);
      setDisplayedBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, []);

  useEffect(() => { fetchBranchesData(); }, [fetchBranchesData]);

  useEffect(() => {
    if (selectedMerchant && selectedMerchant !== "all") {
      setDisplayedBranches(branches?.filter(b => b?.MerchantId == selectedMerchant && b?.active));
    } else {
      setDisplayedBranches(branches);
    }
  }, [selectedMerchant, branches]);

  // ─── Debounce search ──────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ─── Data fetch ───────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminHiddenModifiers({
        merchantId: selectedMerchant,
        branchId: selectedBranch,
        search: debouncedSearch || undefined,
      });
      setRows(data || []);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMerchant, selectedBranch, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Derived counters ─────────────────────────────────────
  const stats = useMemo(() => {
    const branchSet = new Set();
    const productSet = new Set();
    const modifierSet = new Set();
    for (const r of rows) {
      if (r.branchId) branchSet.add(r.branchId);
      if (r.productId) productSet.add(r.productId);
      if (r.modifierId) modifierSet.add(r.modifierId);
    }
    return {
      total: rows.length,
      branches: branchSet.size,
      products: productSet.size,
      modifiers: modifierSet.size,
    };
  }, [rows]);

  // ─── Reusable styles ──────────────────────────────────────
  const filterSelectStyle = {
    padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, fontFamily: "inherit",
    outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 160, transition: "border 0.15s",
  };

  const searchInputStyle = {
    padding: "7px 12px 7px 34px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, fontFamily: "inherit",
    outline: "none", color: C.text, background: C.white, minWidth: 220, transition: "border 0.15s",
  };

  const Pill = ({ children, color, bg }) => (
    <span style={{
      display: "inline-block", padding: "4px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, color, background: bg,
    }}>{children}</span>
  );

  return (
    <Fragment>
      <Breadcrumb title="Hidden Modifiers" parent="Products" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── KPI Tiles ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
            <Tile label="Total Rules" value={stats.total} icon="🔒" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Branches Affected" value={stats.branches} icon="🏬" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={60} />
            <Tile label="Products Affected" value={stats.products} icon="📦" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={120} />
            <Tile label="Modifiers Hidden" value={stats.modifiers} icon="🚫" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={180} />
          </div>

          {/* ─── Filters + Table ────────────────────── */}
          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div onClick={fetchData} title="Refresh"
                style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", background: C.white, flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.coral; e.currentTarget.style.background = C.coralLight; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}>
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <div style={{ position: "relative", display: "inline-block" }}>
                <Search size={14} color={C.textMed} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search product or modifier"
                  style={searchInputStyle}
                />
              </div>

              {((selectedMerchant && selectedMerchant !== "all") || isMerchantScoped(userType)) && (
                <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={filterSelectStyle}>
                  <option value="all">All Branches</option>
                  {displayedBranches?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              )}

              <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>{rows?.length || 0} rules</span>
            </div>

            {isLoading ? <RLoader /> : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Product</th>
                      <th style={tbl.th}>Hidden Modifier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows?.length > 0 ? rows.map(row => (
                      <tr key={row.id} className="rpt-row">
                        <td style={tbl.td}>
                          <div style={{ fontWeight: 700, color: C.text }}>{row?.Branch?.name || "—"}</div>
                          {row?.Branch?.merchant_branch_id && (
                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                              Branch ID: {row.Branch.merchant_branch_id}
                            </div>
                          )}
                        </td>
                        <td style={tbl.td}>
                          <div style={{ fontWeight: 700, color: C.text }}>{row?.Product?.title || "—"}</div>
                          {row?.Product?.arabicName && (
                            <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }} dir="rtl">
                              {row.Product.arabicName}
                            </div>
                          )}
                        </td>
                        <td style={tbl.td}>
                          <Pill color={C.coral} bg={C.coralLight}>
                            {row?.Modifier?.name || "—"}
                          </Pill>
                          {row?.Modifier?.arabicName && (
                            <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }} dir="rtl">
                              {row.Modifier.arabicName}
                            </div>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="3" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>No hidden modifiers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

export default HiddenModifiers;
